import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ListIcon, XIcon } from "@phosphor-icons/react";
import Logo from "@/components/Logo";

const NAV = [
    { to: "/", label: "Home" },
    { to: "/features", label: "Features" },
    { to: "/about", label: "About" },
    { to: "/support", label: "Support" },
    { to: "/contact", label: "Contact" },
];

export default function SiteLayout({ children }) {
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    return (
        <div className="min-h-screen flex flex-col bg-bg text-white">
            <header className="sticky top-0 z-40 glass border-b">
                <div className="max-w-7xl mx-auto px-5 md:px-10 py-3.5 flex items-center justify-between">
                    <Link to="/" data-testid="nav-logo" className="block">
                        <Logo size={36} textSize="text-2xl" />
                    </Link>

                    <nav className="hidden md:flex items-center gap-1">
                        {NAV.map((n) => (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                end={n.to === "/"}
                                data-testid={`nav-${n.label.toLowerCase()}`}
                                className={({ isActive }) =>
                                    `px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                                        isActive
                                            ? "bg-white/10 text-white"
                                            : "text-text-secondary hover:text-white hover:bg-white/5"
                                    }`
                                }
                            >
                                {n.label}
                            </NavLink>
                        ))}
                    </nav>

                    <Link
                        to="/#download"
                        data-testid="nav-download-btn"
                        className="hidden md:inline-flex btn-primary px-5 py-2.5 text-sm"
                    >
                        Download App
                    </Link>

                    <button
                        className="md:hidden p-2 rounded-full hover:bg-white/10"
                        onClick={() => setOpen((o) => !o)}
                        aria-label="Menu"
                        data-testid="nav-menu-toggle"
                    >
                        {open ? <XIcon size={22} /> : <ListIcon size={22} />}
                    </button>
                </div>

                {open && (
                    <div
                        className="md:hidden border-t border-white/5 bg-bg/95 backdrop-blur-xl"
                        data-testid="mobile-menu"
                    >
                        <div className="px-5 py-4 flex flex-col gap-1">
                            {NAV.map((n) => (
                                <NavLink
                                    key={n.to}
                                    to={n.to}
                                    end={n.to === "/"}
                                    data-testid={`nav-mobile-${n.label.toLowerCase()}`}
                                    className={({ isActive }) =>
                                        `px-4 py-3 rounded-2xl font-semibold ${
                                            isActive ? "bg-white/10 text-white" : "text-text-secondary"
                                        }`
                                    }
                                >
                                    {n.label}
                                </NavLink>
                            ))}
                            <Link
                                to="/#download"
                                className="btn-primary mt-3 py-3 text-center"
                                data-testid="nav-mobile-download"
                            >
                                Download App
                            </Link>
                        </div>
                    </div>
                )}
            </header>

            <main className="flex-1">{children}</main>

            <footer className="mt-20 border-t border-white/5 bg-surface">
                <div className="max-w-7xl mx-auto px-5 md:px-10 py-14 grid md:grid-cols-5 gap-8">
                    <div className="md:col-span-2">
                        <Link to="/" className="inline-block">
                            <Logo size={40} textSize="text-2xl" />
                        </Link>
                        <p className="text-text-secondary mt-5 max-w-md text-sm leading-relaxed">
                            India&apos;s own short-video reels platform. Made in India, for India.
                            Bollywood, cricket, street food, shayari — sab kuch ek scroll par.
                        </p>
                        <div className="mt-6 flex gap-3">
                            <a
                                href="https://twitter.com"
                                target="_blank"
                                rel="noreferrer"
                                className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-text-secondary hover:text-white hover:border-white/30 transition-colors text-sm font-bold"
                                data-testid="social-twitter"
                            >
                                X
                            </a>
                            <a
                                href="https://instagram.com"
                                target="_blank"
                                rel="noreferrer"
                                className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-text-secondary hover:text-white hover:border-white/30 transition-colors text-sm font-bold"
                                data-testid="social-instagram"
                            >
                                IG
                            </a>
                            <a
                                href="https://youtube.com"
                                target="_blank"
                                rel="noreferrer"
                                className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-text-secondary hover:text-white hover:border-white/30 transition-colors text-sm font-bold"
                                data-testid="social-youtube"
                            >
                                YT
                            </a>
                        </div>
                    </div>

                    <div>
                        <h4 className="font-heading font-bold text-[11px] uppercase tracking-[0.25em] text-secondary mb-4">
                            Product
                        </h4>
                        <ul className="space-y-2.5 text-sm">
                            <li><FooterLink to="/features">Features</FooterLink></li>
                            <li><FooterLink to="/#download">Download App</FooterLink></li>
                            <li><FooterLink to="/support">Support</FooterLink></li>
                        </ul>
                    </div>

                    <div>
                        <h4 className="font-heading font-bold text-[11px] uppercase tracking-[0.25em] text-secondary mb-4">
                            Company
                        </h4>
                        <ul className="space-y-2.5 text-sm">
                            <li><FooterLink to="/about">About Us</FooterLink></li>
                            <li><FooterLink to="/contact">Contact</FooterLink></li>
                            <li><FooterLink to="/community-guidelines">Community Guidelines</FooterLink></li>
                        </ul>
                    </div>

                    <div>
                        <h4 className="font-heading font-bold text-[11px] uppercase tracking-[0.25em] text-secondary mb-4">
                            Legal
                        </h4>
                        <ul className="space-y-2.5 text-sm">
                            <li><FooterLink to="/privacy">Privacy Policy</FooterLink></li>
                            <li><FooterLink to="/terms">Terms &amp; Conditions</FooterLink></li>
                            <li><FooterLink to="/delete-account">Delete Account</FooterLink></li>
                        </ul>
                    </div>
                </div>
                <div className="border-t border-white/5">
                    <div className="max-w-7xl mx-auto px-5 md:px-10 py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-text-secondary">
                        <div>
                            © {new Date().getFullYear()} India Reels. Made with{" "}
                            <span className="text-primary">♥</span> in India.
                        </div>
                        <div className="flex items-center gap-3">
                            <span>india-reels.com</span>
                            <span className="text-white/20">·</span>
                            <Link
                                to="/admin/login"
                                className="hover:text-white transition-colors"
                                data-testid="footer-admin-link"
                            >
                                Admin
                            </Link>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}

function FooterLink({ to, children }) {
    return (
        <Link
            to={to}
            className="text-text-secondary hover:text-white transition-colors"
            data-testid={`footer-link-${String(children).toLowerCase().replace(/[^a-z]+/g, "-")}`}
        >
            {children}
        </Link>
    );
}
